import { PopoverProps } from "@mui/material/Popover";
import { SxProps, Theme } from "@mui/material/styles";
import { MouseEvent, useCallback, useMemo, useState } from "react";

export type TriggerProps<T> = {
  id: string;
  "aria-controls": string;
  "aria-expanded": "true" | undefined;
  "aria-haspopup": boolean;
  "data-testid": string;
  onClick: (event: MouseEvent<T>) => void;
};

export type MenuProps = {
  id: string;
  "data-testid": string;
  open: boolean;
  anchorEl: PopoverProps["anchorEl"] | null;
  onClose: () => void;
  MenuListProps: { "aria-labelledby": string };
  sx?: SxProps<Theme>;
};

export type UseActionMenu<T> = {
  triggerProps: TriggerProps<T>;
  menuProps: MenuProps;
};

const makeId = (control: string, name: string, id?: string) =>
  `${name}-${control}${id ? `-${id}` : ""}`;

export const useMenu = <T extends HTMLElement>(name: string, id?: string): UseActionMenu<T> => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = useMemo(() => Boolean(anchorEl), [anchorEl]);
  const onClick = useCallback(
    (event: MouseEvent<T>) => setAnchorEl(event.currentTarget),
    [setAnchorEl],
  );
  const onClose = useCallback(() => setAnchorEl(null), [setAnchorEl]);
  const triggerId = useMemo(() => makeId("MenuTrigger", name, id), [id, name]);
  const menuId = useMemo(() => makeId("Menu", name, id), [id, name]);

  const triggerProps = useMemo(
    () => ({
      onClick,
      id: triggerId,
      "aria-controls": menuId,
      "aria-haspopup": true,
      "aria-expanded": open ? ("true" as const) : undefined,
      "data-testid": triggerId,
    }),
    [menuId, onClick, open, triggerId],
  );

  const menuProps = useMemo(
    () => ({
      open,
      onClose,
      anchorEl,
      id: menuId,
      "data-testid": menuId,
      MenuListProps: { "aria-labelledby": triggerId },
    }),
    [anchorEl, menuId, onClose, open, triggerId],
  );

  return useMemo(() => ({ triggerProps, menuProps }), [menuProps, triggerProps]);
};
